import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { ITreatmentSummary } from "../../../@types/admin/AdminTreatment";
import { TableSorted } from "../../../components/TableSorted";
import { useTreatmentsSummary } from "../../../hooks/queries/admin/treatments";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  EmailLink,
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { CountCell } from "./AdminPatients.styles";
import { TreatmentErrorsModal } from "./TreatmentErrorsModal";

const SEARCH_FIELDS: (keyof ITreatmentSummary)[] = [
  "patientId",
  "hospitalPatientId",
  "firstName",
  "lastName",
  "email",
  "serialNumber",
];

/**
 * Treatments admin page (EN-5156), migrated from the Retool Treatments app.
 * Lists nebulizer treatment and error counts per patient and drills into one
 * patient's error log — read-only, through the same `/Retool/Treatments`
 * endpoints the Retool app uses.
 *
 * Retool opened the error log on a double-click with no other affordance;
 * the portal gives the row an explicit "View errors" action so the drill-down
 * is discoverable, and disables it when the patient has no errors.
 */
export function AdminTreatments() {
  document.title = "Treatments - Admin";

  const { openModal } = useModal();
  const { treatmentsSummary, isLoading, isError } = useTreatmentsSummary();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: treatmentsSummary,
    searchFields: SEARCH_FIELDS,
  });

  const handleViewErrors = (patient: ITreatmentSummary) => {
    openModal(<TreatmentErrorsModal patient={patient} />, {
      width: "min(1000px, 94vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<ITreatmentSummary, string>[] = useMemo(
    () => [
      {
        accessorKey: "patientId",
        header: "Patient ID",
        cell: ({ row }) => <span>{row.original.patientId}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "hospitalPatientId",
        header: "MRN",
        enableSorting: true,
      },
      { accessorKey: "firstName", header: "First name", enableSorting: true },
      { accessorKey: "lastName", header: "Last name", enableSorting: true },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.email}
          </EmailLink>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "serialNumber",
        header: "Serial number",
        enableSorting: true,
      },
      {
        // `totalErrors` is 0 for most patients, and TableSorted dashes a
        // falsy value — the accessor renders the count as text so a genuine
        // zero shows as "0" rather than "-".
        id: "totalErrors",
        header: "Total errors",
        accessorFn: (summary) => String(summary.totalErrors ?? 0),
        cell: ({ row }) => (
          <CountCell
            tone={row.original.totalErrors > 0 ? "warning" : "neutral"}
          >
            {row.original.totalErrors}
          </CountCell>
        ),
        enableSorting: true,
      },
      {
        id: "totalTreatments",
        header: "Total treatments",
        accessorFn: (summary) => String(summary.totalTreatments ?? 0),
        enableSorting: true,
      },
      {
        accessorKey: "lastTreatmentDate",
        header: "Last treatment date",
        cell: ({ row }) => (
          <span>
            {getLongFormattedDate(row.original.lastTreatmentDate, "-")}
          </span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowAction
            label="View errors"
            icon={<ExclamationTriangleIcon />}
            disabled={row.original.totalErrors === 0}
            onClick={() => {
              handleViewErrors(row.original);
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Treatments</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by patient ID, MRN, name, email, or serial number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the treatments summary. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-treatments-summary"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "treatments",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
